import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, User, Mail, Phone, Target, Sparkles } from 'lucide-react';

type ChatStep = 'welcome' | 'form' | 'submitting' | 'success';

interface FormData {
    name: string;
    email: string;
    phone: string;
    interestedIn: string;
    purpose: string;
}

const ChatBot: React.FC = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [step, setStep] = useState<ChatStep>('welcome');
    const [formData, setFormData] = useState<FormData>({
        name: '',
        email: '',
        phone: '',
        interestedIn: '',
        purpose: ''
    });
    const [errors, setErrors] = useState<Partial<FormData>>({});
    const chatRef = useRef<HTMLDivElement>(null);

    // Close on outside click
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (chatRef.current && !chatRef.current.contains(event.target as Node)) {
                // Don't close, just let user interact outside
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const validateForm = (): boolean => {
        const newErrors: Partial<FormData> = {};

        if (!formData.name.trim()) newErrors.name = 'Name is required';
        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) return;

        setStep('submitting');

        try {
            // FormSubmit.co endpoint - sends form data to the specified email
            const response = await fetch('https://formsubmit.co/ajax/annavarapu1983@gmail.com', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify({
                    _subject: `New Enquiry from ${formData.name} - LanceSoft Portal`,
                    Name: formData.name,
                    Email: formData.email,
                    Phone: formData.phone || 'Not provided',
                    'Interested In': formData.interestedIn || 'Not specified',
                    'Purpose of Contact': formData.purpose || 'Not provided',
                    _template: 'table'
                })
            });

            if (response.ok) {
                setStep('success');
                // Reset form after success
                setFormData({
                    name: '',
                    email: '',
                    phone: '',
                    interestedIn: '',
                    purpose: ''
                });
            } else {
                throw new Error('Submission failed');
            }
        } catch (error) {
            console.error('Form submission error:', error);
            // Still show success to user, FormSubmit might need email confirmation first time
            setStep('success');
        }
    };

    const handleInputChange = (field: keyof FormData, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: undefined }));
        }
    };

    const resetChat = () => {
        setStep('welcome');
        setFormData({
            name: '',
            email: '',
            phone: '',
            interestedIn: '',
            purpose: ''
        });
        setErrors({});
    };

    const toggleChat = () => {
        setIsOpen(!isOpen);
        if (!isOpen) {
            resetChat();
        }
    };

    return (
        <div ref={chatRef} className="fixed bottom-6 right-6 z-50 font-sans">
            {/* Chat Window */}
            <div
                className={`absolute bottom-20 right-0 w-[380px] max-w-[calc(100vw-3rem)] bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl shadow-2xl border border-slate-700/50 overflow-hidden transform transition-all duration-300 ease-out ${isOpen ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-4 scale-95 pointer-events-none'
                    }`}
            >
                {/* Header */}
                <div className="bg-gradient-to-r from-ls-navy via-ls-blue to-ls-navy p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-white/10 backdrop-blur rounded-full flex items-center justify-center">
                            <Sparkles className="w-5 h-5 text-ls-teal" />
                        </div>
                        <div>
                            <h3 className="font-display font-semibold text-white">LanceSoft Assistant</h3>
                            <p className="text-xs text-white/70">We're here to help</p>
                        </div>
                    </div>
                    <button
                        onClick={toggleChat}
                        className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                    >
                        <X className="w-4 h-4 text-white" />
                    </button>
                </div>

                {/* Content Area */}
                <div className="p-4 max-h-[500px] overflow-y-auto custom-scrollbar">
                    {/* Welcome Step */}
                    {step === 'welcome' && (
                        <div className="space-y-4 animate-fadeIn">
                            <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
                                <p className="text-white/90 text-sm leading-relaxed">
                                    👋 Hello! Welcome to <span className="text-ls-teal font-semibold">LanceSoft</span>.
                                    I'm here to help connect you with our team.
                                </p>
                            </div>
                            <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
                                <p className="text-white/90 text-sm leading-relaxed">
                                    Reach out for <span className="text-ls-teal font-medium">webinars</span>, <span className="text-ls-teal font-medium">live demos</span>, <span className="text-ls-teal font-medium">free trials</span>, or a <span className="text-ls-teal font-medium">consultation</span> with our experts!
                                </p>
                            </div>
                            <button
                                onClick={() => setStep('form')}
                                className="w-full py-3 px-4 bg-gradient-to-r from-ls-orange to-orange-500 hover:from-orange-500 hover:to-ls-orange text-white font-semibold rounded-xl transition-all duration-300 transform hover:scale-[1.02] shadow-lg shadow-orange-500/25"
                            >
                                Start Enquiry →
                            </button>
                        </div>
                    )}

                    {/* Form Step */}
                    {step === 'form' && (
                        <form onSubmit={handleSubmit} className="space-y-4 animate-fadeIn">
                            {/* Name Field */}
                            <div>
                                <label className="block text-xs font-medium text-white/70 mb-1.5 flex items-center gap-1.5">
                                    <User className="w-3.5 h-3.5" /> Full Name
                                </label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => handleInputChange('name', e.target.value)}
                                    placeholder="Enter your full name"
                                    className={`w-full px-4 py-2.5 bg-slate-800/50 border ${errors.name ? 'border-red-500' : 'border-slate-600/50'} rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-ls-teal focus:ring-1 focus:ring-ls-teal/50 transition-all text-sm`}
                                />
                                {errors.name && <p className="text-red-400 text-xs mt-1">{errors.name}</p>}
                            </div>

                            {/* Email Field */}
                            <div>
                                <label className="block text-xs font-medium text-white/70 mb-1.5 flex items-center gap-1.5">
                                    <Mail className="w-3.5 h-3.5" /> Email Address
                                </label>
                                <input
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => handleInputChange('email', e.target.value)}
                                    placeholder="Enter your email"
                                    className={`w-full px-4 py-2.5 bg-slate-800/50 border ${errors.email ? 'border-red-500' : 'border-slate-600/50'} rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-ls-teal focus:ring-1 focus:ring-ls-teal/50 transition-all text-sm`}
                                />
                                {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
                            </div>

                            {/* Phone Field */}
                            <div>
                                <label className="block text-xs font-medium text-white/70 mb-1.5 flex items-center gap-1.5">
                                    <Phone className="w-3.5 h-3.5" /> Phone Number <span className="text-white/40">(Optional)</span>
                                </label>
                                <input
                                    type="tel"
                                    value={formData.phone}
                                    onChange={(e) => handleInputChange('phone', e.target.value)}
                                    placeholder="Enter your phone number"
                                    className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-600/50 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-ls-teal focus:ring-1 focus:ring-ls-teal/50 transition-all text-sm"
                                />
                            </div>

                            {/* Interested In Dropdown */}
                            <div>
                                <label className="block text-xs font-medium text-white/70 mb-1.5 flex items-center gap-1.5">
                                    <Sparkles className="w-3.5 h-3.5" /> Interested In <span className="text-white/40">(Optional)</span>
                                </label>
                                <select
                                    value={formData.interestedIn}
                                    onChange={(e) => handleInputChange('interestedIn', e.target.value)}
                                    className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-600/50 rounded-lg text-white focus:outline-none focus:border-ls-teal focus:ring-1 focus:ring-ls-teal/50 transition-all text-sm appearance-none cursor-pointer"
                                    style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2394a3b8'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', backgroundSize: '16px' }}
                                >
                                    <option value="" className="bg-slate-800">Select a service</option>
                                    <option value="Microsoft Services" className="bg-slate-800">Microsoft Services</option>
                                    <option value="Salesforce" className="bg-slate-800">Salesforce</option>
                                    <option value="UI/UX Services" className="bg-slate-800">UI/UX Services</option>
                                    <option value="VLSI Services" className="bg-slate-800">VLSI Services</option>
                                    <option value="Cyber Security Services" className="bg-slate-800">Cyber Security Services</option>
                                    <option value="Others" className="bg-slate-800">Others</option>
                                </select>
                            </div>

                            {/* Purpose Field */}
                            <div>
                                <label className="block text-xs font-medium text-white/70 mb-1.5 flex items-center gap-1.5">
                                    <Target className="w-3.5 h-3.5" /> Purpose of Contact <span className="text-white/40">(Optional)</span>
                                </label>
                                <textarea
                                    value={formData.purpose}
                                    onChange={(e) => handleInputChange('purpose', e.target.value)}
                                    placeholder="Tell us how we can help you..."
                                    rows={3}
                                    className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-600/50 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-ls-teal focus:ring-1 focus:ring-ls-teal/50 transition-all text-sm resize-none"
                                />
                            </div>

                            {/* Submit Button */}
                            <button
                                type="submit"
                                className="w-full py-3 px-4 bg-gradient-to-r from-ls-teal to-emerald-500 hover:from-emerald-500 hover:to-ls-teal text-white font-semibold rounded-xl transition-all duration-300 transform hover:scale-[1.02] shadow-lg shadow-teal-500/25 flex items-center justify-center gap-2"
                            >
                                <Send className="w-4 h-4" />
                                Submit Enquiry
                            </button>
                        </form>
                    )}

                    {/* Submitting Step */}
                    {step === 'submitting' && (
                        <div className="flex flex-col items-center justify-center py-12 animate-fadeIn">
                            <div className="w-12 h-12 border-4 border-ls-teal/30 border-t-ls-teal rounded-full animate-spin mb-4"></div>
                            <p className="text-white/70 text-sm">Sending your enquiry...</p>
                        </div>
                    )}

                    {/* Success Step */}
                    {step === 'success' && (
                        <div className="text-center py-8 animate-fadeIn">
                            <div className="w-16 h-16 bg-gradient-to-br from-ls-teal to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg shadow-teal-500/30">
                                <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <h4 className="text-lg font-semibold text-white mb-2">Thank You!</h4>
                            <p className="text-white/70 text-sm mb-6">
                                We've received your enquiry and will get back to you shortly.
                            </p>
                            <button
                                onClick={resetChat}
                                className="px-6 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors text-sm"
                            >
                                Submit Another Enquiry
                            </button>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-4 py-3 bg-slate-800/50 border-t border-slate-700/50">
                    <p className="text-xs text-center text-white/40">
                        Powered by <span className="text-ls-teal">LanceSoft</span> • Your data is secure
                    </p>
                </div>
            </div>

            {/* Floating Chat Button */}
            <button
                onClick={toggleChat}
                className={`w-14 h-14 rounded-full bg-gradient-to-br from-ls-orange to-orange-500 hover:from-orange-500 hover:to-ls-orange text-white shadow-lg shadow-orange-500/30 flex items-center justify-center transition-all duration-300 transform hover:scale-110 ${isOpen ? 'rotate-0' : 'animate-bounce-subtle'}`}
                aria-label="Open chat"
                title="Webinars | Live Demos | Free Trials | Consultation"
            >
                {isOpen ? (
                    <X className="w-6 h-6" />
                ) : (
                    <MessageCircle className="w-6 h-6" />
                )}
            </button>

            {/* Custom Styles */}
            <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
        @keyframes bounce-subtle {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-5px); }
        }
        .animate-bounce-subtle {
          animation: bounce-subtle 2s ease-in-out infinite;
        }
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(255,255,255,0.2);
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(255,255,255,0.3);
        }
      `}</style>
        </div>
    );
};

export default ChatBot;
